import {json, type LoaderArgs} from '@shopify/remix-oxygen';
import {useLoaderData} from '@remix-run/react';
import type {Collection as CollectionType} from '@shopify/hydrogen/storefront-api-types';
import {AnalyticsPageType} from '@shopify/hydrogen';
import invariant from 'tiny-invariant';
import {PRODUCT_CARD_FRAGMENT} from '~/data/fragments';
import {CACHE_SHORT, routeHeaders} from '~/data/cache';
import {seoPayload} from '~/lib/seo.server';

//domains
import type {BFSResponse} from 'domains/collections';
import {
  useDefaultAdsQuery,
  PRODUCT_PER_PAGE,
  PRODUCT_PER_PAGE_WITHOUT_ADS,
} from 'domains/collections';
import {truncate} from 'domains/global';

//components
import Container from 'components/Container';
import CollectionMain from 'components/collection/CollectionMain';

//translation
import translation from 'translations/collection.json';
import tagTranslation from 'translations/tags.json';
import {routeLocale} from 'domains/global';

export const headers = routeHeaders;

export type SortParam =
  | 'price-low-high'
  | 'price-high-low'
  | 'best-selling'
  | 'newest'
  | 'featured';

export async function loader({params, request, context}: LoaderArgs) {
  const searchParams = new URL(request.url).searchParams;
  const requestedVendors = searchParams
    ?.getAll('pf_v_brand')
    ?.map((b) => `pf_v_brand[]=${b}`)
    ?.join(`&`);

  const trimmedCollectionId = '0';

  const queryParamsArray = Array.from(searchParams.entries()).map(
    ([key, value]) => {
      if (key === 'pf_v_brand') {
        return `pf_v_brand[]=${value}`;
      }
      if (key === 'pf_opt_size') {
        return `pf_opt_size[]=${value}`;
      }
      if (key === 'pf_t_style') {
        return `pf_t_style[]=${value}`;
      }
      if (key === 'pf_t_tag') {
        return `pf_t_tag[]=${value}`;
      }
      if (key === 'pf_t_color') {
        return `pf_t_color[]=${value}`;
      }
      if (key === 'pf_p_price') {
        return `pf_p_price[]=${value}`;
      }
      if (key === 'pf_st_stock_status') {
        const stockStatus = value === 'in-stock' ? 'true' : 'false';
        return `pf_st_stock_status[]=${stockStatus}`;
      }
      if (key === 'page') {
        return `page=${value ?? 1}`;
      }
      if (key === 'sort') {
        return `sort=${value ?? 'manual'}`;
      }
      return `${key}[]=${value}`;
    },
  );

  const stockStatusKey = 'pf_st_stock_status[]=';
  if (!queryParamsArray.some((item) => item.startsWith(stockStatusKey))) {
    queryParamsArray.push(`${stockStatusKey}true`);
  }

  const queryParamsObj = new URLSearchParams();

  queryParamsArray.map((v: string) => {
    const key = v.split('=')[0];
    const value = v.split('=')[1];
    queryParamsObj.append(key, value);
  });

  // const queryParams = `&${queryParamsArray.join(`&`)}`;
  const queryParams = `&${queryParamsObj.toString()}`;

  const defaultAdsData = await useDefaultAdsQuery();
  const defaultAdvertisement =
    defaultAdsData.defaultAdvertisement.productSettings.product_settings_main;
  const hasAds = defaultAdvertisement?.advertisement?.ad_is_active;
  const productPerPage = hasAds
    ? PRODUCT_PER_PAGE
    : PRODUCT_PER_PAGE_WITHOUT_ADS;

  //fetch from boost commerce
  const bfsResult = await fetch(
    `https://services.mybcapps.com/bc-sf-filter/filter?shop=helmetking-0001.myshopify.com&collection_scope=${trimmedCollectionId}&build_filter_tree=true&limit=${productPerPage}${queryParams}&${requestedVendors}&pf_st_stock_status%5B%5D=true`,
  );
  const bfsData = (await bfsResult.json()) as BFSResponse;

  const seo = {
    title: 'Search Brands',
    description: '',
  };

  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;

  return json(
    {
      analytics: {
        pageType: AnalyticsPageType.page,
      },
      translation: translation[selectedLocale],
      tagTranslation: tagTranslation[selectedLocale],
      hasAds,
      seo,
      defaultAdvertisement,
      products: bfsData.products,
      filter: bfsData.filter,
      totalProducts: bfsData.total_product,
    },
    {
      headers: {
        'Cache-Control': CACHE_SHORT,
      },
    },
  );
}

export default function Collection() {
  // const {collection} = useLoaderData<typeof loader>();

  // if (!collection) {
  //   return <></>;
  // }

  return (
    <section className="lg:pt-10 relative">
      <Container>
        <CollectionMain type="brands" />
      </Container>
    </section>
  );
}

const COLLECTION_QUERY = `#graphql
  query CollectionDetails(
    $handle: String!
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    collection(handle: $handle) {
      id
      handle
      title
      description
      seo {
        description
        title
      }
      image {
        id
        url
        width
        height
        altText
      }
    }
  }
`;
